import { useCallback, useMemo } from 'react'
import { useSnackbar } from '@ui/snackbar'
import { useModelStore, useStiffeningStore } from '@editorStores'
import { useGuidToElementIncludingRips, buildGuidToLabelMap } from '@editorHooks'
import CalculateErrorSnackbarContent from './CalculateErrorSnackbarContent'
import {
  buildCalculateErrorSummaryMessage,
  findElementGuidsInMessageOrdered,
  formatErrorWithGuidLabels,
} from './utils'

const useCalculateSnackbar = () => {
  const { enqueueSnackbar } = useSnackbar()
  const guidToElement = useGuidToElementIncludingRips()
  const model = useModelStore(state => state.model)
  const stiffeningIntervals = useStiffeningStore(state => state.stiffeningIntervals)

  const guidToLabel = useMemo(
    () => buildGuidToLabelMap(model, stiffeningIntervals),
    [model, stiffeningIntervals],
  )

  const elementGuidKeys = useMemo(() => Object.keys(guidToElement), [guidToElement])

  const focusElements = useModelStore(state => state.focusElements)

  const showCalculateSnackbar = useCallback(
    (errorMessage: string) => {
      const actionableGuids = findElementGuidsInMessageOrdered(errorMessage, elementGuidKeys)

      const fullMessage = formatErrorWithGuidLabels(errorMessage, guidToLabel)
      const snackbarKey = `calculate-error-${Date.now()}`

      if (actionableGuids.length > 0) {
        const summaryMessage = buildCalculateErrorSummaryMessage(actionableGuids[0], guidToLabel)
        enqueueSnackbar(
          <CalculateErrorSnackbarContent
            snackbarKey={snackbarKey}
            summaryMessage={summaryMessage}
            fullMessage={fullMessage}
            actionableGuids={actionableGuids}
            guidToLabel={guidToLabel}
            onAction={guid => focusElements([guid])}
          />,
          { variant: 'error', key: snackbarKey, action: () => null },
        )
      } else {
        enqueueSnackbar(fullMessage, { variant: 'error', key: snackbarKey })
      }
    },
    [elementGuidKeys, enqueueSnackbar, focusElements, guidToLabel],
  )

  return { showCalculateSnackbar }
}

export default useCalculateSnackbar
